import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { setTenantHint } from "@/lib/tenant";
import { usePlanStatus, LIMIT_LABELS } from "@/lib/usePlanStatus";

export const ImpersonationBar = () => {
  const { user, brand } = useAuth();
  if (!user?.impersonator) return null;
  const back = async () => {
    try {
      const { data } = await api.post("/platform/return");
      setTenantHint("");
      window.location.href = data.relogin ? "/login" : "/plattform";
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  return (
    <div className="sticky top-0 z-40 bg-orange-500 text-white px-4 py-2 text-sm flex items-center gap-3 flex-wrap" data-testid="impersonation-bar">
      <Eye size={15} />
      <span>Du siehst Träger <b>{brand?.name || user.tenant}</b> als {user.email} – alle Zugriffe werden protokolliert.</span>
      <button onClick={back} className="ml-auto px-3 py-1 bg-white text-orange-700 text-xs font-semibold hover:bg-orange-50" data-testid="impersonation-return">Zurück zur Plattform</button>
    </div>
  );
};

export const PlanWarning = () => {
  const { user } = useAuth();
  const st = usePlanStatus(!!user?.tenant);
  if (!user?.tenant || !st || (!st.warnings.length && !st.expired && !st.payment_failed_at)) return null;
  const full = st.full.length > 0;
  return (
    <Link to="/traeger?tab=tarif" className={`mx-3 mb-2 block border px-3 py-2 text-[11px] leading-snug ${full || st.expired || st.payment_failed_at ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="plan-warning">
      <div className="font-semibold flex items-center gap-1.5"><AlertTriangle size={12} /> {st.locked ? "Zugang gesperrt – Zahlung offen" : st.payment_failed_at ? "Zahlung fehlgeschlagen" : st.expired ? "Tarif abgelaufen" : full ? "Limit erreicht" : "Limit fast erreicht"}</div>
      {st.warnings.map((k) => <div key={k}>{LIMIT_LABELS[k]}: {st.usage[k]} / {st.plan.limits[k]}</div>)}
      <div className="underline mt-0.5">{st.payment_failed_at ? "Zahlungsart aktualisieren" : st.expired ? "Abo starten" : "Tarif upgraden"}</div>
    </Link>
  );
};

export const AuditGuard = ({ children }) => {
  const { user } = useAuth();
  const st = usePlanStatus(!!user?.tenant);
  if (!user?.tenant) return children;
  if (!st) return <div className="p-10 text-xs text-slate-400">Lädt…</div>;
  if (st.plan.features?.audit) return children;
  return (
    <div className="max-w-lg mx-auto p-10 text-center" data-testid="audit-locked">
      <AlertTriangle size={36} className="mx-auto text-amber-500" />
      <h1 className="font-heading text-2xl mt-4">Nicht in deinem Tarif enthalten</h1>
      <p className="text-sm text-slate-500 mt-1">Das Interne Audit (Umfragen, Unterlagen-Prüfung, Auditübersicht, Verbleibsquote) ist im Tarif {st.plan.name} nicht enthalten.</p>
      <Link to="/traeger?tab=tarif" className="inline-block mt-6 px-5 py-2.5 bg-slate-900 text-white text-sm" data-testid="audit-upgrade">Tarif upgraden</Link>
    </div>
  );
};

export const LockGate = ({ children }) => {
  const { user } = useAuth();
  const st = usePlanStatus(!!user?.tenant);
  const onTarif = window.location.pathname.startsWith("/traeger") || window.location.pathname.startsWith("/payment");
  if (!user?.tenant || onTarif) return children;
  if (!st) return <div className="p-10 text-xs text-slate-400">Lädt…</div>;
  if (!st.locked) return children;
  const isAdmin = user.role === "admin" || user.is_master_admin;
  return (
    <div className="max-w-lg mx-auto p-10 text-center" data-testid="tenant-locked">
      <AlertTriangle size={36} className="mx-auto text-red-600" />
      <h1 className="font-heading text-2xl mt-4">Zugang gesperrt</h1>
      <p className="text-sm text-slate-500 mt-1">Die Zahlung für den Tarif ist seit über 7 Tagen offen. {isAdmin ? "Bitte aktualisiere die Zahlungsart." : "Bitte wende dich an deinen Träger-Admin."}</p>
      {isAdmin && <Link to="/traeger?tab=tarif" className="inline-block mt-6 px-5 py-2.5 bg-slate-900 text-white text-sm" data-testid="tenant-locked-fix">Zum Tarif</Link>}
    </div>
  );
};
